import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { adminApi } from '@/lib/api'
import { apiKeysFrom, sortKeys, type ApiKey, type ApiKeyWithToken, type CreateApiKeyRequest } from './apiKeys'

/**
 * The account's API keys.
 *
 * `/v1/admin/api-keys` reads as an admin route and is not one: the gateway
 * opens it to every authenticated caller and scopes the listing to them
 * (an administrator, and only an administrator, sees every account's keys).
 * The calls still live in `@/lib/api/admin.ts` — that module is not this
 * wave's to reorganise, and it is the one place these paths are written down.
 */
export const API_KEYS_QUERY_KEY = ['einstellungen', 'api-keys'] as const

export function useApiKeys() {
  return useQuery<ApiKey[]>({
    queryKey: API_KEYS_QUERY_KEY,
    queryFn: async () => sortKeys(apiKeysFrom(await adminApi.listAPIKeys())),
  })
}

export function useCreateApiKey() {
  return useMutation<ApiKeyWithToken, unknown, CreateApiKeyRequest>({
    mutationFn: (body) => adminApi.createAPIKey(body) as Promise<ApiKeyWithToken>,
    // Deliberately no `onSuccess` refetch here: the freshly created key's
    // token lives in the dialog's state, and a refetch that re-renders the
    // page while it is on screen is exactly how a once-only secret gets lost.
    // The section invalidates once the dialog is closed.
  })
}

export function useRevokeApiKey() {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, string>({
    mutationFn: (keyId) => adminApi.deleteAPIKey(keyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: API_KEYS_QUERY_KEY })
    },
  })
}
